import { useEffect, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { motion, useScroll } from "framer-motion";
import Navbar from "@/components/landing/Navbar";
import Hero from "@/components/landing/Hero";
import Marquee from "@/components/landing/Marquee";
import Manifesto from "@/components/landing/Manifesto";
import CTABand from "@/components/landing/CTABand";
import Courses from "@/components/landing/Courses";
import Accreditation from "@/components/landing/Accreditation";
import Placements from "@/components/landing/Placements";
import SocialStrip from "@/components/landing/SocialStrip";
import FAQ from "@/components/landing/FAQ";
import QueryForm from "@/components/landing/QueryForm";
import Footer from "@/components/landing/Footer";
import MobileCTA from "@/components/landing/MobileCTA";
import FloatingWhatsApp from "@/components/landing/FloatingWhatsApp";
import ScholarshipExam from "@/pages/ScholarshipExam";
import AdminScholarships from "@/pages/AdminScholarships";
import AdmissionForm from "@/pages/AdmissionForm";

function Landing() {
  const lenisRef = useRef(null);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const scrollTo = useCallback((id) => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(id, { offset: -72, immediate: id === "#enquire" });
    } else {
      document.querySelector(id)?.scrollIntoView({ behavior: "auto" });
    }
  }, []);

  return (
    <div className="min-h-screen bg-brand-bg font-sans text-brand-ink antialiased">
      <div className="grain-overlay" aria-hidden="true" />
      <motion.div
        data-testid="scroll-progress"
        style={{ scaleX: scrollYProgress }}
        className="fixed inset-x-0 top-0 z-[70] h-[3px] origin-left bg-brand-terra"
      />
      <Navbar scrollTo={scrollTo} />
      <main>
        <Hero scrollTo={scrollTo} />
        <Marquee />
        <Manifesto />
        <CTABand />
        <Courses scrollTo={scrollTo} />
        <Accreditation />
        <Placements scrollTo={scrollTo} />
        <SocialStrip />
        <FAQ />
        <QueryForm />
      </main>
      <Footer scrollTo={scrollTo} />
      <div className="h-24 md:hidden" />
      <MobileCTA scrollTo={scrollTo} />
      <FloatingWhatsApp />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/scholarship-exam" element={<ScholarshipExam />} />
        <Route path="/admission-form" element={<AdmissionForm />} />
        <Route path="/admin/scholarships" element={<AdminScholarships />} />
      </Routes>
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}
